import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import { NotificationService } from "../services/notification";
import { debounce } from "lodash";
import { clearAllStorage } from "../utils/storageUtils";
import { getApiErrorMessage, isBookingHasPayment, isPassError } from "../utils/apiErrors";
import { shouldEndSession, isRejectedButSignedIn, REJECTED_BUT_SIGNED_IN_MESSAGE } from "../utils/sessionErrors";
import { getFreshToken, refreshStoredToken, refreshWasRejected } from "../utils/freshToken";

const instance = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/crud`,
});

// Instance without /crud prefix for direct API endpoints
const directInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}`,
});

const debouncedNotification = debounce((message: string) => {
  NotificationService.error(message);
}, 300);

// Handle token expiration and auth conflicts
const handleTokenExpiration = () => {
  console.log("Token expired or auth conflict detected, clearing all storage...");
  clearAllStorage();
  // SAM-240: force a full reload (rather than an in-SPA state flip) so every
  // module-level flag (isBusinessDataLoaded here and in businessAccessClient,
  // plus their request queues) resets to a clean slate before the login form
  // shows. The query param lets LayoutPage surface an explicit "session
  // expired" message instead of a silent redirect.
  window.location.href = "/?session_expired=1";
};

// Create request queue to handle requests before business data is loaded
let isBusinessDataLoaded = false;
const requestQueue: (() => void)[] = [];

// Function to process the queue
export const processRequestQueue = () => {
  isBusinessDataLoaded = true;
  while (requestQueue.length > 0) {
    const request = requestQueue.shift();
    if (request) request();
  }
};

// Request interceptor function to reuse for both instances
const requestInterceptor = async (config: any) => {
  // Get token, refreshed first if it expired (SAM-766)
  const token = await getFreshToken();
  
  if (!token) {
    // No token, no need to add API key
    return config;
  }

  config.headers.Authorization = `Bearer ${token}`;
  config.headers["Content-Type"] = "application/json";
  
  // If business data is already loaded, proceed immediately
  if (isBusinessDataLoaded) {
    // Get API key from session storage
    let apiKey;
    const businessData = sessionStorage.getItem("businessData");
    if (businessData) {
      try {
        const parsedData = JSON.parse(businessData);
        apiKey = parsedData.api_key;
        config.headers["X-Api-Key"] = apiKey;
      } catch (e) {
        console.error("Error parsing business data from sessionStorage", e);
      }
    }
    
    // Fallback to a default API key if needed
    if (!apiKey) {
      apiKey = import.meta.env.VITE_API_KEY;
      console.warn("Using default API key - business data not available");
      config.headers["X-Api-Key"] = apiKey;
    }
    
    return config;
  }

  // Business data not loaded yet, wait until it's available
  return new Promise((resolve) => {
    requestQueue.push(() => {
      // Get API key from session storage after it's loaded
      let apiKey;
      const businessData = sessionStorage.getItem("businessData");
      if (businessData) {
        try {
          const parsedData = JSON.parse(businessData);
          apiKey = parsedData.api_key;
          config.headers["X-Api-Key"] = apiKey;
        } catch (e) {
          console.error("Error parsing business data from sessionStorage", e);
        }
      }
      
      // Fallback to a default API key if needed
      if (!apiKey) {
        apiKey = import.meta.env.VITE_API_KEY;
        console.warn("Using default API key - business data not available");
        config.headers["X-Api-Key"] = apiKey;
      }
      
      resolve(config);
    });
  });
};

// Response interceptor function to reuse for both instances
const responseInterceptor = {
  onFulfilled: (response: any) => {
    if (response.data) {
      return response.data;
    }
    return response;
  },
  onRejected: async (error: any) => {
    // SAM-766: a 401 gets one token refresh and a retry before anything else —
    // the authorizer on /sam/* rejects expired tokens (also when the local
    // clock is off and the token still looks valid here).
    const original = error?.config;
    const unauthorized = error?.response?.status === 401 || shouldEndSession(error, localStorage.getItem("token"));
    if (original && !original._sessionRetried && unauthorized) {
      original._sessionRetried = true;
      const fresh = await refreshStoredToken();
      if (fresh) {
        original.headers = { ...original.headers, Authorization: `Bearer ${fresh}` };
        const client = original.baseURL === instance.defaults.baseURL ? instance : directInstance;
        return client.request(original);
      }
      // The server turned the refresh token down: the session is over, even if this token's `exp` is still ahead
      // (revoked, signed out elsewhere, clock off). A refresh that failed on the network does not end it.
      if (refreshWasRejected()) {
        handleTokenExpiration();
        return Promise.reject(error);
      }
    }
    try {
      // NB: the `finally` below is what rejects. Returning a second
      // `Promise.reject(error)` from inside the `try` would leave an orphan
      // rejected promise behind (the `finally` return replaces it) and the
      // browser reports it as "Uncaught (in promise)" even when the caller
      // handles the real one.
      if (axios.isCancel(error)) {
        return;
      }

      // Only sign out when the session is really over — a 401 from one endpoint
      // with a still-valid token is that endpoint's problem, not a logout.
      const token = localStorage.getItem("token");
      if (shouldEndSession(error, token)) {
        handleTokenExpiration();
        return;
      }
      if (isRejectedButSignedIn(error, token)) {
        debouncedNotification(REJECTED_BUT_SIGNED_IN_MESSAGE);
        return;
      }

      // Suppress notifications for search and user-lookup endpoints (expected failures)
      const requestUrl = error.config?.url || '';
      // SAM-629: reset-password and verified-document callers map the backend error codes to
      // their own toast, so the generic one would only duplicate it.
      const isSilentEndpoint = requestUrl.includes('/users/search') || requestUrl.includes('/crud/user') ||
        /\/(reset-password|verified-document)$/.test(requestUrl);

      // SAM-568 — a 504 (or network timeout, which axios surfaces with no `response` at all) on
      // the async engine-sync POST is not a failure: the server may still finish past API
      // Gateway's 29s limit. `useSyncServiceToEngine` falls back to polling GET instead of
      // reporting an error, so this generic toast must not jump in ahead of it and say otherwise.
      const isEngineSyncGatewayTimeout =
        requestUrl.includes('/bookings/services/') &&
        requestUrl.includes('/engine-sync') &&
        (error.response?.status === 504 || !error.response);

      // Same shape for PUT /user_estate: the row is updated before the slow push
      // fan-out, so a 504 there is "verify", not "failed" — useUpdateUserEstateStatus
      // reads the row back and reports the real outcome itself.
      const isEstateStatusGatewayTimeout =
        error.config?.method?.toLowerCase() === 'put' &&
        /\/user_estate\/?$/.test(requestUrl) &&
        (error.response?.status === 504 || !error.response);

      // S-38: PUT /invitation/{id} answers 409 when it was already cancelled; the caller says so.
      // The 409 with a pass error code (pass_expired, ...) is not that: the generic toast reads it (SAM-981).
      const isInvitationAlreadyCancelled =
        error.config?.method?.toLowerCase() === 'put' &&
        /\/invitation\/[^/]+$/.test(requestUrl) &&
        error.response?.status === 409 &&
        !isPassError(error);

      // SAM-1320 (U41): a caller that shows the failure itself (a notice on the page) marks the request
      // `quietErrors`, and the generic toast would only say it a second time.
      const callerReports = error.config?.quietErrors === true;

      if (callerReports || isSilentEndpoint || isEngineSyncGatewayTimeout || isEstateStatusGatewayTimeout || isInvitationAlreadyCancelled || isBookingHasPayment(error)) {
        return;
      }

      // SAM-544: coherent message for a 403 from the SAM-538 capability
      // checks (and a sane fallback for any other error shape) instead of
      // an undefined/generic toast — see utils/apiErrors.ts.
      debouncedNotification(getApiErrorMessage(error));
    } catch (e) {
      debouncedNotification(error.message);
    } finally {
      return Promise.reject(error);
    }
  }
};

// Add interceptors to both instances
instance.interceptors.request.use(requestInterceptor);
instance.interceptors.response.use(responseInterceptor.onFulfilled, responseInterceptor.onRejected);

directInstance.interceptors.request.use(requestInterceptor);
directInstance.interceptors.response.use(responseInterceptor.onFulfilled, responseInterceptor.onRejected);



export const defaultInstance = <T = unknown>(config: AxiosRequestConfig) => {
  return instance.request<T>(config);
};

export const directApiInstance = <T = unknown>(config: AxiosRequestConfig) => {
  return directInstance.request<T>(config);
};
